import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Divider,
  Flex,
  FlexItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Title,
} from '@patternfly/react-core';
import { useApiFetch } from '../../api';
import { useNotifications } from '../../contexts/NotificationContext';
import { useWallet } from '../../hooks/useWallet';
import type { Invoice, InvoicePayRequest } from '../../types/models/Invoice';
import type { PaymentSource } from '../../types/models/Payment';
import type { ApiResponseInvoicePay } from '../../types/api/ApiResponseInvoice';
import { formatDate } from '../../utils/dateFormat';
import { formatPrice } from '../../utils/money';
import PaymentSourceChooser from './Billing/PaymentSourceChooser';
import StaffAutopayCharge from './StaffAutopayCharge';

interface PayInvoiceModalProps {
  /** The outstanding official invoice to pay; `null` when the modal is closed. */
  invoice: Invoice | null;
  isOpen: boolean;
  onClose: () => void;
  /** Called after a synchronous settlement (wallet, or a card charge Mollie settled inline) so the parent refetches. */
  onPaid: () => void;
  /** Reports whether a payment is being submitted/redirected — the parent aria-disables every row's Pay item meanwhile. */
  onBusyChange: (busy: boolean) => void;
}

type SubmitPhase = 'idle' | 'submitting' | 'redirecting';

/**
 * "Pay invoice" — the payment-source choice for `POST /clients/invoices/{id}/pay`
 * (docs/api-types/clients.md — Pay Invoice): prepaid balance (debited synchronously), the saved
 * card (charged in the background) or a Mollie hosted checkout (redirect). The wallet is fetched
 * on open only (`useWallet(isOpen)`), so the request never fires for users who never pay.
 *
 * On a hosted checkout the modal stays open in a "redirecting" state while `window.location`
 * unloads the page — never close it, or the idle table would flash before navigation.
 *
 * Staff inside an impersonation session also get `StaffAutopayCharge` below the sources: a separate,
 * staff-only charge on the organization's own automatic-payment consent. The sources above keep refusing
 * wallet / card under impersonation; the hosted checkout stays the customer's own path.
 */
const PayInvoiceModal: React.FunctionComponent<PayInvoiceModalProps> = ({
  invoice,
  isOpen,
  onClose,
  onPaid,
  onBusyChange,
}) => {
  const apiFetch = useApiFetch();
  const navigate = useNavigate();
  const { addNotification, setDrawerOpen } = useNotifications();
  const { wallet, defaultPaymentMethod, loading: walletLoading, error: walletError } = useWallet(isOpen);

  const [source, setSource] = useState<PaymentSource>('checkout');
  const [saveCard, setSaveCard] = useState(false);
  const [phase, setPhase] = useState<SubmitPhase>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<string[]>([]);
  // A staff charge on the organization's consent is pending (StaffAutopayCharge).
  const [staffBusy, setStaffBusy] = useState(false);

  // Reset the choice for every invoice the modal opens for — a previous pick must not leak over.
  const invoiceId = invoice?.id ?? null;
  useEffect(() => {
    if (isOpen) {
      setSource('checkout');
      setSaveCard(false);
      setPhase('idle');
      setErrorMessage(null);
      setFieldErrors([]);
      setStaffBusy(false);
    }
  }, [isOpen, invoiceId]);

  const busy = phase !== 'idle' || staffBusy;

  useEffect(() => {
    onBusyChange(busy);
  }, [busy, onBusyChange]);

  const submit = useCallback(async () => {
    if (!invoice) {
      return;
    }
    setPhase('submitting');
    setErrorMessage(null);
    setFieldErrors([]);

    const body: InvoicePayRequest = {
      source,
      ...(source === 'checkout' && saveCard ? { save_card: true } : {}),
    };

    try {
      const response = await apiFetch(`/clients/invoices/${invoice.id}/pay`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const result = (await response.json().catch(() => null)) as ApiResponseInvoicePay | null;

      // Branch on the JSend status, never response.ok — the backend maps controller exceptions to
      // status: 'error' at HTTP 200, so a failure can still carry response.ok === true.
      if (result?.status === 'success' && result.data) {
        const data = result.data;
        if (typeof data.checkout_url === 'string' && data.checkout_url !== '') {
          // Hand off to Mollie's hosted checkout. Stay in 'redirecting' — the page is unloading.
          setPhase('redirecting');
          window.location.href = data.checkout_url;
          return;
        }
        if (data.source === 'wallet' && data.paid === true) {
          addNotification({
            title: `Invoice #${invoice.numero} paid`,
            srTitle: 'Success alert',
            description: `Invoice #${invoice.numero} was paid from your prepaid balance.`,
            variant: 'success',
          });
          setDrawerOpen(true);
          setPhase('idle');
          onPaid();
          onClose();
          return;
        }
        if (data.source === 'card') {
          if (data.paid === true) {
            addNotification({
              title: `Invoice #${invoice.numero} paid`,
              srTitle: 'Success alert',
              description: `Invoice #${invoice.numero} was paid with your saved card.`,
              variant: 'success',
            });
            setDrawerOpen(true);
            setPhase('idle');
            onPaid();
            onClose();
            return;
          }
          // The charge is in flight at Mollie — the return page polls the invoice until the webhook lands.
          addNotification({
            title: 'Card payment in progress',
            srTitle: 'Info alert',
            description: `Your saved card is being charged for invoice #${invoice.numero}. We'll confirm the payment in a moment.`,
            variant: 'info',
          });
          setDrawerOpen(true);
          setPhase('idle');
          navigate(`/payment/return?inv=${invoice.id}`);
          return;
        }
      }

      // JSend error: the contract fixes these messages (insufficient balance, already in progress,
      // declined, impersonation…) — surface them verbatim, and keep the modal open so another
      // source can be picked. A validation failure adds per-field detail under data.*.
      const message = result?.message ?? 'Could not start the payment. Please try again.';
      const details = [
        result?.data?.source,
        result?.data?.save_card,
        result?.data?.payment_method_id,
        result?.data?.body,
      ].filter((v): v is string => typeof v === 'string');
      setErrorMessage(message);
      setFieldErrors(details);
      addNotification({
        title: 'Could not pay invoice',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
      setPhase('idle');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected network error occurred.';
      setErrorMessage(message);
      addNotification({
        title: 'Could not pay invoice',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
      setPhase('idle');
    }
  }, [invoice, source, saveCard, apiFetch, addNotification, setDrawerOpen, onPaid, onClose, navigate]);

  if (!invoice) {
    return null;
  }

  const amountDue = invoice.amounts.solde;
  // Checkout needs nothing from the wallet; the other two sources wait for the balance/card to load.
  const payDisabled = busy || (source !== 'checkout' && walletLoading);

  return (
    <Modal
      variant="medium"
      isOpen={isOpen}
      onClose={busy ? undefined : onClose}
      aria-labelledby="pay-invoice-modal-title"
      aria-describedby="pay-invoice-modal-body"
    >
      <ModalHeader title={`Pay invoice #${invoice.numero}`} labelId="pay-invoice-modal-title" />
      <ModalBody id="pay-invoice-modal-body">
        {phase === 'redirecting' ? (
          <Flex
            direction={{ default: 'column' }}
            alignItems={{ default: 'alignItemsCenter' }}
            spaceItems={{ default: 'spaceItemsMd' }}
          >
            <FlexItem>
              <Spinner size="xl" aria-label="Redirecting to the payment provider" />
            </FlexItem>
            <FlexItem>
              <Content component="p" className="pf-v6-u-text-align-center">
                Redirecting you to our payment provider — please keep this window open.
              </Content>
            </FlexItem>
          </Flex>
        ) : (
          <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsMd' }}>
            <FlexItem>
              <DescriptionList isHorizontal isCompact>
                <DescriptionListGroup>
                  <DescriptionListTerm>Invoice</DescriptionListTerm>
                  <DescriptionListDescription>{invoice.intitule}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Date</DescriptionListTerm>
                  <DescriptionListDescription>{formatDate(invoice.date)}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Amount due</DescriptionListTerm>
                  <DescriptionListDescription>
                    <strong>{formatPrice(amountDue)}</strong>
                  </DescriptionListDescription>
                </DescriptionListGroup>
              </DescriptionList>
            </FlexItem>
            <Divider />
            <FlexItem>
              <Title headingLevel="h3" size="md" className="pf-v6-u-mb-sm">
                Pay with
              </Title>
              <PaymentSourceChooser
                idPrefix="pay-invoice"
                value={source}
                onChange={setSource}
                saveCard={saveCard}
                onSaveCardChange={setSaveCard}
                amountDue={amountDue}
                wallet={wallet}
                defaultPaymentMethod={defaultPaymentMethod}
                walletLoading={walletLoading}
                walletError={walletError}
                isDisabled={busy}
              />
            </FlexItem>
            {errorMessage && (
              <FlexItem>
                <Alert variant="danger" isInline title={errorMessage}>
                  {fieldErrors.length > 0 && (
                    <ul>
                      {fieldErrors.map((detail) => (
                        <li key={detail}>{detail}</li>
                      ))}
                    </ul>
                  )}
                </Alert>
              </FlexItem>
            )}
            <StaffAutopayCharge
              invoice={invoice}
              isOpen={isOpen}
              isDisabled={phase !== 'idle'}
              onBusyChange={setStaffBusy}
              onPaid={onPaid}
              onClose={onClose}
            />
          </Flex>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          onClick={() => void submit()}
          isLoading={phase === 'submitting'}
          isDisabled={payDisabled}
        >
          Pay {formatPrice(amountDue)}
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={busy}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PayInvoiceModal;
